/**
 * Guided add-line flow (design doc, "Guided add-line flow"): "הוסף שורה" now
 * walks old device -> new device -> count/ratio instead of landing a blank
 * line and leaving every column to be filled in by hand.
 *
 * Three `Stepper` steps in one small modal; each step's own picker (old
 * device via `OldDeviceField`'s own `ItemPicker`, new device via a second one
 * here) is the exact same catalog picker the plain grid cells use — this
 * wizard only sequences them and collects what they return. It never
 * computes `required`/`take`/`to_order`/cost itself: those stay
 * server-derived (`utils/replacement_engine.py`), exactly like every other
 * number on this screen (see `ReplacementPlanEditor.tsx`'s own header
 * comment) — the caller appends the collected inputs as a new line and saves
 * immediately, and the real figures appear once that response lands.
 */
import { useEffect, useState } from 'react'
import {
  Alert, Button, Group, NumberInput, Select, Stack, Stepper, Text,
} from '@mantine/core'

import type { InventoryLine, ItemRow } from '../api/types'
import { Modal } from './AppModal'
import { OldDeviceField, type OldDeviceValue } from './OldDeviceField'
import { ItemPicker } from '../pages/ItemPicker'

export interface GuidedLineInput {
  old_label: string
  old_sku: string | null
  old_qty: number
  ratio: number
  /** The picked new item, as a full catalog row — the caller builds the new
   * line from this the same way the plain "בחר פריט…" picker does
   * (`ReplacementPlanEditor.tsx`'s own `blankLine()`), so there is one place
   * that turns an `ItemRow` into a line's `new_item_id`/`item_name`/
   * `item_manufacturer`, not two. */
  item: ItemRow
  /** The chosen term (DP-11), when `item.is_termed` — null otherwise, and
   * also null for a termed item until the operator picks one on the count
   * step (the line still saves; it comes back `blocked` until they do). */
  term_months: number | null
}

interface Props {
  opened: boolean
  onClose: () => void
  onSubmit: (line: GuidedLineInput) => void
  /** The plan's currently selected snapshot's lines, for the old-device
   * on-hand hint in step 1 — same prop `OldDeviceField` takes directly. */
  snapshotLines?: InventoryLine[]
}

const EMPTY_OLD: OldDeviceValue = { old_label: '', old_sku: null }

export function ReplacementLineWizard({
  opened, onClose, onSubmit, snapshotLines,
}: Props) {
  const [step, setStep] = useState(0)
  const [oldDevice, setOldDevice] = useState<OldDeviceValue>(EMPTY_OLD)
  const [newItem, setNewItem] = useState<ItemRow | null>(null)
  const [pickingNew, setPickingNew] = useState(false)
  const [oldQty, setOldQty] = useState(0)
  const [ratio, setRatio] = useState(1)
  const [termMonths, setTermMonths] = useState<number | null>(null)

  // A freshly-picked item's terms replace whatever a PREVIOUS pick left
  // selected — going back to step 2 and choosing a different (or untermed)
  // item must not leave a stale term_months from the one before it.
  useEffect(() => {
    setTermMonths(null)
  }, [newItem])

  const oldOk = oldDevice.old_label.trim().length > 0
  const newOk = newItem != null
  const qtyOk = oldQty > 0
  const ratioOk = ratio > 0

  function reset() {
    setStep(0); setOldDevice(EMPTY_OLD); setNewItem(null)
    setOldQty(0); setRatio(1); setTermMonths(null)
  }

  function close() {
    reset()
    onClose()
  }

  function submit() {
    if (!oldOk || !newOk || !qtyOk || !ratioOk || !newItem) return
    onSubmit({
      old_label: oldDevice.old_label,
      old_sku: oldDevice.old_sku,
      old_qty: oldQty,
      ratio,
      item: newItem,
      term_months: termMonths,
    })
    close()
  }

  return (
    <Modal opened={opened} onClose={close} title="הוספת שורה מונחית" size="lg">
      <Stack>
        <Stepper
          active={step}
          size="sm"
          allowNextStepsSelect={false}
          onStepClick={(idx) => {
            // Backward jumps are always fine (reviewing what was already
            // entered); a forward jump is only honoured once its own
            // prerequisite holds — the same gate the "הבא" button below
            // applies, so the two navigation paths never disagree about
            // which step is reachable yet.
            if (idx <= step) { setStep(idx); return }
            if (idx === 1 && oldOk) setStep(idx)
            else if (idx === 2 && oldOk && newOk) setStep(idx)
          }}
        >
          <Stepper.Step label="מכשיר ישן" description="מה מוחלף">
            <Stack mt="sm">
              <Text size="sm" c="dimmed">
                בחר את המכשיר הישן מהקטלוג, או הזן את שמו ידנית אם אינו בקטלוג.
              </Text>
              <OldDeviceField
                value={oldDevice}
                onChange={(patch) => setOldDevice((v) => ({ ...v, ...patch }))}
                snapshotLines={snapshotLines}
                size="sm"
              />
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="מכשיר חדש" description="מה מחליף">
            <Stack mt="sm" align="center">
              <Text size="sm" c="dimmed">בחר את הפריט החדש מהקטלוג שיחליף את המכשיר הישן.</Text>
              <Button
                size="sm"
                variant={newItem ? 'light' : 'filled'}
                onClick={() => setPickingNew(true)}
              >
                {newItem
                  ? `${newItem.name}${newItem.manufacturer ? ` (${newItem.manufacturer})` : ''}`
                  : 'בחר פריט חדש…'}
              </Button>
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="כמות" description="כמה ויחס">
            <Stack mt="sm">
              <Text size="sm" c="dimmed">
                כמה מכשירים מהסוג הישן יש ברשת, וכמה חדשים מחליפים כל אחד מהם.
              </Text>
              <Group grow align="flex-start">
                <NumberInput
                  label="כמות ברשת" hideControls min={0} decimalScale={2}
                  value={oldQty} onChange={(v) => setOldQty(Number(v) || 0)}
                  autoFocus
                />
                <NumberInput
                  label="יחס (חדש לכל ישן)" hideControls min={0.01} decimalScale={2}
                  value={ratio} onChange={(v) => setRatio(Number(v) || 1)}
                />
              </Group>
              {/* DP-11: the new item carries a licence/support term that must
                  be chosen before the line can be priced — same rule as a BOM
                  line's own term Select (BomDetail.tsx). Left unset here, the
                  line still saves; it just comes back blocked until it is
                  set, same as every other way to reach this state. */}
              {newItem?.is_termed && (
                <Select
                  label="תקופה"
                  placeholder="בחר תקופה"
                  data={newItem.terms_available.map((t) => ({
                    value: String(t), label: `${t} חודשים`,
                  }))}
                  value={termMonths != null ? String(termMonths) : null}
                  onChange={(v) => setTermMonths(v ? Number(v) : null)}
                />
              )}
              {newItem?.is_termed && termMonths == null && (
                <Alert color="yellow" variant="light" p="xs">
                  הפריט "{newItem.name}" מחייב בחירת תקופה — השורה תתווסף
                  חסומה עד שתיבחר תקופה.
                </Alert>
              )}
            </Stack>
          </Stepper.Step>
        </Stepper>

        <Group justify="space-between">
          <Button variant="default" onClick={() => (step === 0 ? close() : setStep((s) => s - 1))}>
            {step === 0 ? 'ביטול' : 'הקודם'}
          </Button>
          {step < 2 ? (
            <Button
              onClick={() => setStep((s) => s + 1)}
              disabled={(step === 0 && !oldOk) || (step === 1 && !newOk)}
            >
              הבא
            </Button>
          ) : (
            <Button onClick={submit} disabled={!qtyOk || !ratioOk}>הוסף שורה</Button>
          )}
        </Group>
      </Stack>

      <ItemPicker
        opened={pickingNew}
        onClose={() => setPickingNew(false)}
        onPick={(items) => {
          const item = items[0]
          setPickingNew(false)
          if (item) setNewItem(item)
        }}
        title="בחירת פריט חדש"
        confirmLabel="בחר"
      />
    </Modal>
  )
}
